import {useEffect} from 'react'
import {useUser} from '@supabase/auth-helpers-react'
import {useTranslate} from '@tolgee/react'
import {enqueueSnackbar} from 'notistack'
import monitoring from '../utils/monitoring'
import {capture_event} from '../lib/posthog'
import {
  clear_pending_quiz_result,
  read_pending_quiz_result,
} from '../utils/pendingQuizResult'

/**
 * Attaches a quiz result taken anonymously with an existing account's email
 * once that person is signed in (normally right after the sign-in link the
 * quiz mailed them). App-level rather than inside the quiz: the quiz lives in
 * a modal that is closed when they land back on the page.
 *
 * The server attaches only when the verified session email equals the email
 * the claim was minted for, and only an anonymous row, once.
 */
export default function PendingQuizResultClaimer() {
  const user = useUser()
  const {t} = useTranslate()

  useEffect(() => {
    if (!user?.id) return
    const result_claim = read_pending_quiz_result()
    if (!result_claim) return

    const claim = async () => {
      try {
        const response = await fetch('/api/quiz/claim-result', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({result_claim}),
        })
        // 5xx / 429: transient, keep the claim for the next page load.
        if (response.status >= 500 || response.status === 429) return
        // Anything else is final: attached, already attached, expired, or a
        // different account is signed in.
        clear_pending_quiz_result()
        if (!response.ok) return
        const data = await response.json()
        capture_event('questionnaire_result_claimed', {
          attached: !!data?.attached,
        })
        if (data?.attached) {
          enqueueSnackbar({
            message: t('components.quiz.results.savedToAccount'),
            variant: 'success',
          })
        }
      } catch (error) {
        monitoring.failure('quiz-claim-result', String(error))
      }
    }
    claim()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  return null
}
